import type { PaintingRecord, TrackRecord } from "../types";

/** 每幅画最多推荐几首；实际可能只有 1 首 */
export const TRACKS_PER_PAINTING_MAX = 2;

/** 第二首得分须达到第一首的比例，否则只保留一首 */
const SECOND_TRACK_MIN_SCORE_RATIO = 0.72;

/** 全馆已选次数 → 扣分，避免几十幅画共用同一两首 */
const USAGE_PENALTY = 14;

function audioBasename(t: TrackRecord): string {
  const raw = (t.audioFile || "").trim().replace(/^\/audio\//i, "");
  return raw.split("?")[0]!.toLowerCase();
}

/** 曲名与音频文件名是否对得上（过滤「安魂曲」却链到土耳其进行曲等元数据错误） */
function titleMatchesAudioFile(t: TrackRecord): boolean {
  const title = (t.title || "").toLowerCase();
  const file = audioBasename(t);
  if (!file) return false;

  if (/安魂|落泪之日|lacrimosa|requiem/i.test(title)) {
    return /requiem|lacrimosa|dies/i.test(file) && !/turkish|march|rondo/i.test(file);
  }
  if (/荣耀经|gloria/i.test(title)) {
    return /gloria|vivaldi|bach.*mass/i.test(file) && !/beethoven|allegro|pathetique/i.test(file);
  }
  if (/爱之梦|liebestraum/i.test(title)) {
    return /liebestraum|lovedream|love/i.test(file);
  }
  if (/土耳其|turkish|rondo alla turca/i.test(title)) {
    return /turkish|turca|rondo/i.test(file);
  }
  if (/水上|water music/i.test(title)) {
    return /water|handel/i.test(file);
  }
  if (/四季.*春/i.test(title) || (/spring/i.test(title) && /四季|vivaldi/i.test(title))) {
    return /spring/i.test(file) && !/winter|summer|autumn|fall/i.test(file);
  }
  if (/四季.*夏/i.test(title)) {
    return /summer/i.test(file);
  }
  if (/四季.*秋/i.test(title)) {
    return /autumn|fall/i.test(file);
  }
  if (/四季.*冬/i.test(title)) {
    return /winter/i.test(file) && !/spring|summer|autumn/i.test(file);
  }
  if (/天鹅湖|swan lake/i.test(title)) {
    return /swan/i.test(file);
  }
  if (/clair de lune|月光/i.test(title) && !/奏鸣曲|moonlight/i.test(title)) {
    return /clair|lune/i.test(file) && !/arabesque|moonlight/i.test(file);
  }
  if (/月光奏鸣曲|moonlight/i.test(title)) {
    return /moonlight|op\.?\s*27/i.test(file) && !/symphony|fur.?elise/i.test(file);
  }
  if (/小狗|minute waltz|walzer/i.test(title)) {
    return /minute|puppy|waltz.*chopin|chopin.*waltz/i.test(file);
  }
  if (/水中倒影|reflets/i.test(title)) {
    return /reflet|reflection|debussy/i.test(file) && !/handel|water.?music/i.test(file);
  }
  if (/皇帝协奏曲|emperor/i.test(title)) {
    return /emperor|concerto.*5|op\.?\s*73/i.test(file) && !/moonlight|op\.?\s*27/i.test(file);
  }
  if (/英雄波兰|polonaise/i.test(title)) {
    return /polonaise|heroic|op\.?\s*53/i.test(file);
  }
  if (/雨滴|raindrop/i.test(title)) {
    return /raindrop|prelude.*15|op\.?\s*28/i.test(file);
  }
  if (/勃兰登堡/i.test(title)) {
    return /brandenburg/i.test(file);
  }
  if (/平均律|well-?tempered|wtc/i.test(title)) {
    return /well.?tempered|wtc|bach.*prelude|prelude.*c/i.test(file) && !/clementi|sonatina/i.test(file);
  }
  if (/协奏曲/i.test(title) && /格里格|grieg/i.test(title)) {
    return /grieg/i.test(file);
  }
  if (/协奏曲/i.test(title) && /莫扎特|mozart/i.test(title)) {
    return /mozart/i.test(file) && /concerto|k\.?\s*4/i.test(file) && !/turkish|grieg|beethoven/i.test(file);
  }
  if (/交响曲|symphony/i.test(title)) {
    if (/命运|第五/i.test(title)) return /symphony|fate|symphony5|no\.?\s*5/i.test(file);
    if (/悲怆/i.test(title) && /柴可夫斯基|tchaikovsky/i.test(t.composer || "")) {
      return /pathetique|pathétique|symphony.*6|no\.?\s*6/i.test(file) && !/waltz|swan|flower/i.test(file);
    }
    if (/欢乐颂|第九/i.test(title)) return /ode|joy|symphony.*9|no\.?\s*9/i.test(file);
  }

  return true;
}

function titleAudioFitScore(t: TrackRecord): number {
  const title = (t.title || "").toLowerCase();
  const file = audioBasename(t);
  let score = 0;
  const pairs: [RegExp, RegExp][] = [
    [/土耳其/, /turkish|turca/],
    [/水上/, /water|handel/],
    [/四季.*春/, /spring|vivaldi/],
    [/四季.*冬/, /winter|vivaldi/],
    [/四季.*夏/, /summer|vivaldi/],
    [/四季.*秋/, /autumn|fall|vivaldi/],
    [/小夜曲/, /nachtmusik|serenade|schubert|mozart/],
    [/前奏曲|赋格/, /prelude|bach|fugue/],
    [/柔板|咏叹/, /adagio|albinoni|aria/],
    [/夜曲/, /nocturne|chopin/],
    [/月光/, /moonlight|debussy|arabesque/],
    [/圆舞曲|春之声/, /waltz|strauss/],
    [/爱之梦/, /liebestraum|lovedream/],
  ];
  for (const [reT, reF] of pairs) {
    if (reT.test(title) && reF.test(file)) score += 5;
  }
  return score;
}

/**
 * 仅保留曲库中「确有音频且曲名与文件一致」的条目；
 * 同一 mp3 只保留最匹配曲名的一条（避免安魂曲/土耳其进行曲共文件）。
 */
export function filterTracksForPairing(tracks: TrackRecord[]): TrackRecord[] {
  const withAudio = tracks.filter((t) => (t.audioFile || "").trim().length > 0);
  const trustworthy = withAudio.filter(titleMatchesAudioFile);

  const byFile = new Map<string, TrackRecord[]>();
  for (const t of trustworthy) {
    const key = audioBasename(t);
    const list = byFile.get(key) ?? [];
    list.push(t);
    byFile.set(key, list);
  }

  const canonical: TrackRecord[] = [];
  for (const group of byFile.values()) {
    group.sort(
      (a, b) =>
        titleAudioFitScore(b) - titleAudioFitScore(a) || a.id - b.id
    );
    canonical.push(group[0]!);
  }

  return canonical.sort((a, b) => a.id - b.id);
}

/** 名画时期 → 可相配的音乐时期（按策展优先级） */
export const PAINTING_ERA_TO_MUSIC_ERAS: Record<string, string[]> = {
  文艺复兴: ["巴洛克", "古典主义"],
  巴洛克: ["巴洛克"],
  浪漫主义: ["浪漫主义"],
  现实主义: ["浪漫主义"],
  印象派: ["印象派", "浪漫主义"],
  后印象派: ["印象派", "浪漫主义"],
  现代艺术: [],
};

type Theme =
  | "water"
  | "nature"
  | "sacred"
  | "portrait"
  | "dramatic"
  | "dance"
  | "night"
  | "court"
  | "love";

/** 策展指定：画作 id → 优先曲目 id（按顺序） */
const PAINTING_TRACK_BOOST: Record<number, number[]> = {
  53: [16], // 《吻》→ 李斯特《爱之梦》
};

/** 策展指定：画作 id → 禁止搭配的曲目 id */
const PAINTING_TRACK_EXCLUDE: Record<number, number[]> = {
  52: [16], // 《呐喊》不用《爱之梦》
};

const PAINTING_MOOD_BY_ERA: Record<string, string> = {
  文艺复兴: "人文理想、庄严构图与含蓄的神秘感",
  巴洛克: "戏剧性的明暗对比与宏大的叙事张力",
  浪漫主义: "激情、个体情感与历史或自然的崇高",
  现实主义: "日常生活的质朴、劳作与宁静秩序",
  印象派: "流动的光色、空气感与瞬间的视觉印象",
  后印象派: "强烈的情绪色彩与主观化的造型",
};

const PAINTING_MOOD_BY_ID: Record<number, string> = {
  1: "神秘微笑、柔和晕染与静谧的凝视感",
  2: "宗教叙事中群像的庄严与空间深度",
  3: "神圣叙事中人物动态的张力与空间的庄严",
  4: "哲学思辨与古典理想的宏大群像",
  5: "神话诞生与肉体美的古典抒情",
  8: "奇幻寓言与细密叙事的梦境感",
  11: "圣母与圣子的温柔神圣",
  13: "群体肖像的动感与明暗戏剧",
  14: "侧光下少女的含蓄与珍珠的一点高光",
  15: "室内静物与晨光的日常诗意",
  18: "宫廷空间的纵深与人物凝视",
  21: "革命浪潮中前进的身影与炽烈的色彩",
  24: "雾海与孤独旅人，崇高而克制的自然意境",
  26: "风暴海面的动荡与自然力量的压迫感",
  27: "田野劳作与大地节律的沉稳、朴实",
  32: "睡莲与水面倒影的朦胧、冥想式宁静",
  35: "向日葵的饱满色块与生命力的昂扬",
  36: "旋涡状星空与村庄的梦幻、内心骚动",
  39: "树影斑驳下的舞会、轻盈的世俗欢愉",
  52: "扭曲天空与呐喊式的内在焦虑",
  53: "金箔装饰下恋人拥吻的缠绵与装饰性的爱欲",
};

export function normalizeMusicEra(era: string | undefined): string {
  const e = (era || "").trim();
  if (e === "古典主义/浪漫主义") return "古典主义";
  return e;
}

function paintingYear(p: PaintingRecord): number | null {
  const y = p.year;
  if (typeof y === "number" && Number.isFinite(y)) return y;
  if (typeof y === "string") {
    const n = Number.parseInt(y, 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function paintingText(p: PaintingRecord): string {
  return `${p.title} ${p.intro} ${p.story ?? ""} ${p.technique ?? ""}`;
}

function trackText(t: TrackRecord): string {
  return `${t.title} ${(t.tags ?? []).join(" ")} ${t.introBlurb ?? ""}`;
}

function detectPaintingThemes(p: PaintingRecord): Set<Theme> {
  const t = paintingText(p);
  const themes = new Set<Theme>();
  if (/海|水|诞生|沐浴|泰晤士|渔|船|湖|河/.test(t)) themes.add("water");
  if (/园|乐园|春|花|田园|拾穗|睡莲|向日葵|田野|麦|树|林|风景|雾海|旅人/.test(t)) {
    themes.add("nature");
  }
  if (/圣母|晚餐|创造|基督|圣经|祭祀|西斯廷|哲学|神/.test(t)) themes.add("sacred");
  if (/肖像|少女|夫妇|自画像|微笑|珍珠|大使|宫女|头像/.test(t)) themes.add("portrait");
  if (/夜|暗|月光|烛/.test(t)) themes.add("night");
  if (/战|革命|自由|凌|刑|美杜莎|夜巡|奴隶|呐喊/.test(t)) themes.add("dramatic");
  if (/舞|宴会|磨坊|庆典/.test(t)) themes.add("dance");
  if (/宫|帝王|大使/.test(t)) themes.add("court");
  if (/吻|拥吻|恋人|爱情|爱欲|情欲/.test(t)) themes.add("love");
  return themes;
}

function detectTrackThemes(t: TrackRecord): Set<Theme> {
  const s = trackText(t);
  const themes = new Set<Theme>();
  if (/水上|水|河|船/.test(s)) themes.add("water");
  if (/四季.*春|春之声|春|田园|乡村|田野|柔板|G弦|小夜曲|摇篮|田园/.test(s)) themes.add("nature");
  if (/荣耀经|受难|弥撒|安魂|宗教/.test(s)) themes.add("sacred");
  if (/夜曲|小夜曲|月光|爱丽丝|阿拉伯|前奏曲|柔板|咏叹/.test(s)) themes.add("portrait");
  if (/夜|月/.test(s)) themes.add("night");
  if (/交响|命运|悲怆|革命|进行曲|冬|凌/.test(s)) themes.add("dramatic");
  if (/圆舞|舞曲|舞会/.test(s)) themes.add("dance");
  if (/土耳其|宫廷|皇帝|brandenburg|勃兰登堡/i.test(s)) themes.add("court");
  if (/爱之梦|爱情|liebestraum/i.test(s)) themes.add("love");
  return themes;
}

function themeOverlapBonus(p: PaintingRecord, t: TrackRecord): number {
  const pt = detectPaintingThemes(p);
  const tt = detectTrackThemes(t);
  let bonus = 0;
  for (const theme of pt) {
    if (tt.has(theme)) bonus += 14;
  }
  return bonus;
}

/** 无「水/自然」主题时，压低水上音乐、四季·春等泛用曲 */
function genericTrackPenalty(p: PaintingRecord, t: TrackRecord): number {
  const pt = detectPaintingThemes(p);
  const title = t.title || "";
  let penalty = 0;
  if (/水上|Water Music/i.test(title) && !pt.has("water") && !pt.has("court")) {
    penalty += 18;
  }
  if (/四季.*春|^春/.test(title) && !pt.has("nature") && !pt.has("dance")) {
    penalty += 14;
  }
  if (/春之声/.test(title) && !pt.has("dance") && !pt.has("nature")) {
    penalty += 12;
  }
  return penalty;
}

function paintingMood(p: PaintingRecord): string {
  return PAINTING_MOOD_BY_ID[p.id] ?? PAINTING_MOOD_BY_ERA[p.era] ?? "画面整体的情绪与笔触气质";
}

function trackMood(t: TrackRecord): string {
  const blob = trackText(t);
  if (/夜曲|小夜曲|月光|摇篮|天鹅|阿拉伯|柔板|咏叹/.test(blob)) {
    return "柔和、抒情或夜色的静谧";
  }
  if (/进行曲|交响曲.*命运|欢乐颂|革命/.test(blob)) {
    return "庄严、动力或集体性的高潮";
  }
  if (/圆舞曲|舞曲|舞会|春之声/.test(blob)) {
    return "旋转的节奏与世俗的欢愉";
  }
  if (/狂想曲|练习曲|匈牙利/.test(blob)) {
    return "炫技与澎湃的激情";
  }
  if (/序曲|幻想|前奏曲|牧神/.test(blob)) {
    return "朦胧的色彩与氛围铺陈";
  }
  const era = normalizeMusicEra(t.era);
  if (era === "印象派") return "漂浮的光影与和声色彩";
  if (era === "巴洛克") return "复调织体与装饰性的华丽";
  if (era === "古典主义") return "均衡的形式与清晰的旋律线条";
  if (era === "浪漫主义") return "个性化的情感宣泄与宽广的旋律";
  return "与作品时期相应的音乐气质";
}

function eraBridgePhrase(paintingEra: string, trackEra: string): string {
  if (paintingEra === trackEra) return `同属${paintingEra}语境`;
  if (paintingEra === "文艺复兴" && trackEra === "巴洛克") {
    return "文艺复兴的人文精神延续至巴洛克的庄严铺陈";
  }
  if (paintingEra === "现实主义" && trackEra === "浪漫主义") {
    return "现实主义画作的田园叙事，与浪漫主义音乐的抒情延展";
  }
  if (
    (paintingEra === "印象派" || paintingEra === "后印象派") &&
    trackEra === "印象派"
  ) {
    return "视觉上的光色实验与音乐中的和声朦胧相互呼应";
  }
  if (
    (paintingEra === "印象派" || paintingEra === "后印象派") &&
    trackEra === "浪漫主义"
  ) {
    return "绘画中残留的情感张力，与浪漫主义音乐的宽广抒情";
  }
  return `${paintingEra}视觉语言与${trackEra}音乐风格的历史关联`;
}

function sharedAspect(p: PaintingRecord, t: TrackRecord): string {
  const pt = detectPaintingThemes(p);
  if (pt.has("sacred")) return "神圣叙事与庄严空间";
  if (pt.has("portrait")) return "凝视、私密与细腻的情绪";
  if (pt.has("water")) return "水波、流动与冥想感";
  if (pt.has("nature")) return "自然、田园与生命力";
  if (pt.has("dramatic")) return "戏剧张力与情感强度";
  if (pt.has("dance")) return "律动、节庆与世俗欢愉";
  if (pt.has("night")) return "夜色、凝神与内省";
  if (pt.has("love")) return "爱恋、缠绵与温柔告白";
  const blob = `${p.title} ${t.title}`;
  if (/革命|自由|交响|命运/.test(blob)) return "历史气魄与集体情感";
  return "情绪氛围与时代精神";
}

function composerHint(composer: string): string {
  if (/德彪西|Debussy/i.test(composer)) return "德彪西式的色彩与氛围";
  if (/莫扎特|Mozart/i.test(composer)) return "莫扎特式的优雅与澄明";
  if (/贝多芬|Beethoven/i.test(composer)) return "贝多芬式的戏剧性与内心冲突";
  if (/肖邦|Chopin/i.test(composer)) return "肖邦式的诗意与钢琴歌唱性";
  if (/柴可夫斯基|Tchaikovsky/i.test(composer)) return "柴可夫斯基式的旋律张力";
  if (/巴赫|Bach/i.test(composer)) return "巴赫式的秩序与纵深";
  if (/维瓦尔第|Vivaldi/i.test(composer)) return "维瓦尔第式的明快与季节感";
  if (/亨德尔|Handel/i.test(composer)) return "亨德尔式的堂皇与庆典感";
  if (/比才|Bizet/i.test(composer)) return "比才式的戏剧张力与吉卜赛风情";
  if (/威尔第|Verdi/i.test(composer)) return "威尔第式的歌剧张力与合唱气势";
  if (/维米尔|Vermeer/i.test(composer)) return "室内般的静谧与细腻";
  return `${composer}的典型风格`;
}

export function explainMatch(painting: PaintingRecord, track: TrackRecord): string {
  const pMood = paintingMood(painting);
  const tMood = trackMood(track);
  const bridge = eraBridgePhrase(painting.era, normalizeMusicEra(track.era));
  const aspect = sharedAspect(painting, track);
  const composer = composerHint(track.composer || "作曲家");

  const py = paintingYear(painting);
  const ty = track.year;
  let timeNote = "";
  if (py != null && ty != null) {
    const gap = Math.abs(py - ty);
    if (gap <= 80) timeNote = "创作年代相近，";
    else if (gap <= 150) timeNote = "虽相隔百余年，";
  }

  return `《${painting.title}》侧重${pMood}；${timeNote}${bridge}。${composer}的《${track.title}》以${tMood}见长，二者在「${aspect}」上形成对照与共鸣。`;
}

export function canPairPainting(p: PaintingRecord, tracks: TrackRecord[]): boolean {
  const eras = PAINTING_ERA_TO_MUSIC_ERAS[p.era];
  if (!eras?.length) return false;
  return tracks.some((t) => eras.includes(normalizeMusicEra(t.era)));
}

function curatedTrackBonus(paintingId: number, trackId: number): number {
  const list = PAINTING_TRACK_BOOST[paintingId];
  if (!list?.length) return 0;
  const idx = list.indexOf(trackId);
  if (idx === -1) return 0;
  return 48 - idx * 6;
}

function isExcludedTrack(paintingId: number, trackId: number): boolean {
  return PAINTING_TRACK_EXCLUDE[paintingId]?.includes(trackId) ?? false;
}

function scorePair(
  p: PaintingRecord,
  t: TrackRecord,
  usage: Map<number, number>
): number {
  if (isExcludedTrack(p.id, t.id)) return -1;

  const musicEras = PAINTING_ERA_TO_MUSIC_ERAS[p.era];
  if (!musicEras?.length) return -1;

  const trackEra = normalizeMusicEra(t.era);
  if (!musicEras.includes(trackEra)) return -1;

  let score = musicEras[0] === trackEra ? 14 : 9;

  const py = paintingYear(p);
  const ty = t.year;
  if (py != null && ty != null) {
    const gap = Math.abs(py - ty);
    if (gap <= 50) score += 6;
    else if (gap <= 100) score += 4;
    else if (gap <= 160) score += 2;
  }

  score += themeOverlapBonus(p, t);
  score += curatedTrackBonus(p.id, t.id);
  score -= genericTrackPenalty(p, t);
  score -= (usage.get(t.id) ?? 0) * USAGE_PENALTY;

  if (p.era.includes("印象") && trackEra === "印象派") score += 6;
  if (p.era === "浪漫主义" && trackEra === "浪漫主义") score += 3;

  return score;
}

function composerKey(t: TrackRecord): string {
  return (t.composer || "").trim().toLowerCase();
}

function trackLabelKey(t: TrackRecord): string {
  return `${composerKey(t)}::${(t.title || "").replace(/\s+/g, "").toLowerCase()}`;
}

/** 选 1～2 首：得分最高；第二首仅在足够匹配且作曲家不同时才加入 */
export function pickTracksForPainting(
  painting: PaintingRecord,
  tracks: TrackRecord[],
  usage: Map<number, number>,
  limit = TRACKS_PER_PAINTING_MAX
): TrackRecord[] {
  const ranked = tracks
    .map((t) => ({ t, score: scorePair(painting, t, usage) }))
    .filter((x) => x.score >= 0)
    .sort((a, b) => b.score - a.score || a.t.id - b.t.id);

  if (!ranked.length) return [];

  const bestScore = ranked[0]!.score;
  const out: TrackRecord[] = [ranked[0]!.t];
  const firstComposer = composerKey(ranked[0]!.t);
  const seenLabels = new Set([trackLabelKey(ranked[0]!.t)]);

  for (let i = 1; i < ranked.length && out.length < limit; i++) {
    const { t, score } = ranked[i]!;
    if (out.some((x) => x.id === t.id)) continue;
    const label = trackLabelKey(t);
    if (seenLabels.has(label)) continue;
    if (score < bestScore * SECOND_TRACK_MIN_SCORE_RATIO) break;
    if (composerKey(t) === firstComposer && score < bestScore * 0.85) continue;
    seenLabels.add(label);
    out.push(t);
    break;
  }

  return out;
}

export interface TrackMatch {
  track: TrackRecord;
  reason: string;
}

export interface PaintingWithTracks {
  painting: PaintingRecord;
  matches: TrackMatch[];
}

export function buildPaintingPairings(
  paintings: PaintingRecord[],
  tracks: TrackRecord[],
  tracksPerPainting = TRACKS_PER_PAINTING_MAX
): PaintingWithTracks[] {
  const pool = filterTracksForPairing(tracks);
  const usage = new Map<number, number>();
  const eligible = paintings
    .filter((p) => canPairPainting(p, pool))
    .sort((a, b) => a.id - b.id);

  const results: PaintingWithTracks[] = [];

  for (const painting of eligible) {
    const picked = pickTracksForPainting(painting, pool, usage, tracksPerPainting);
    if (!picked.length) continue;

    for (const track of picked) {
      usage.set(track.id, (usage.get(track.id) ?? 0) + 1);
    }

    results.push({
      painting,
      matches: picked.map((track) => ({
        track,
        reason: explainMatch(painting, track),
      })),
    });
  }

  return results;
}
